import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import _ from 'lodash';

import { generateKey, errorLogger } from '@/utils';
import DropdownMenu from '../../../components/DropdownMenu/DropdownMenu';
import Image from '../../../components/ContentfulImage';
import Placeholder from '../../../components/Placeholder';
import Spinner from '../../../components/Spinner';
import { fetchNewsAndEvents } from '../../../redux/actions/newsAndEvents';
import { getArrayNewsAndEvents } from '../../../redux/selectors/pages';
import { getContent } from '../../../services/api';

import {
  ListSection,
  EntryContainer,
  Entry,
  Intro,
  Title,
  Details,
  Type,
  Description,
  Date,
  Button,
  ButtonLabel,
} from './ListSection.style';

/**
 * 「NEWS & EVENTS」トップ画面 > イベントリスト、「もっと見る」ボタン表示
 */
class List extends React.Component {
  static defaultProps = {
    copy: {},
  };

  static propTypes = {
    copy: PropTypes.shape(),
    entries: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    fetchNewsAndEvents: PropTypes.func.isRequired,
    locale: PropTypes.string.isRequired,
  };

  state = {
    dropdowns: [{ id: 'location', active: [], options: [] }, { id: 'type', active: [], options: [] }],
    entries: [{ key: 1 }, { key: 2 }, { key: 3 }, { key: 4 }],
    hasMore: true,
    loadingMore: false,
    menuLoaded: false,
    showing: 4,
  };

  componentDidMount() {
    const { menuLoaded } = this.state;
    const { entries, fetchNewsAndEvents, locale } = this.props;

    if (entries.length < 4) {
      fetchNewsAndEvents({ limit: 4, locale, skip: 0 })
        .then(res => {
          if (res.value.length < 4) this.setState({ hasMore: false });
          return res;
        })
        .catch(e => {
          errorLogger(e);
        });
    } else {
      this.updateEntries();
    }

    if (!menuLoaded)
      getContent({ contentType: 'newsAndEventsMenu', locale })
        .then(data =>
          this.setState(state => ({
            dropdowns: state.dropdowns.map(dropdown => ({
              ...dropdown,
              options: data[dropdown.id],
            })),
            menuLoaded: true,
          }))
        )
        .catch(errorLogger);
  }

  componentDidUpdate = prevProps => {
    const { entries } = this.props;
    const { entries: prevEntries } = prevProps;

    if (entries.length > prevEntries.length) this.updateEntries();
  };

  filterEntries = entries => {
    const { dropdowns } = this.state;

    return entries.filter(entry =>
      dropdowns
        .map(dd => dd.active.length === 0 || dd.active.some(filter => filter === entry[dd.id]))
        .every(result => result)
    );
  };

  handleLoadMore = () => {
    const { dropdowns, entries } = this.state;
    const { entries: allEntries, fetchNewsAndEvents, locale } = this.props;

    const availableEntries = this.filterEntries(allEntries).length - entries.length;
    if (availableEntries >= 4) {
      this.setState(state => ({ showing: state.showing + 4 }));
    } else {
      const filters = dropdowns.reduce((filters, current) => {
        if (current.active.length > 0) {
          return { ...filters, [current.id]: current.active.join(',') };
        }
        return filters;
      }, {});

      this.setState({ loadingMore: true }, () => {
        fetchNewsAndEvents({ filters, limit: 4, locale, skip: entries.length }).then(res => {
          if (res.value.data.length < 4) {
            this.setState(({ showing }) => ({ hasMore: false, loadingMore: false, showing: showing + 4 }));
          } else {
            this.setState(({ showing }) => ({ loadingMore: false, showing: showing + 4 }));
          }
          return res;
        });
      });
    }
  };

  handleUpdateDropdown = dropdowns => {
    this.setState({ dropdowns, hasMore: true, showing: 4 }, () => this.updateEntries());
  };

  updateEntries = () => {
    const { entries: allEntries } = this.props;

    const entries = this.filterEntries(allEntries);
    this.setState({ entries });
  };

  render = () => {
    const { dropdowns, entries, hasMore, loadingMore, showing } = this.state;
    const { copy, locale } = this.props;

    // 日付順ソート
    entries.sort((a, b) => {
      if (a.date > b.date) {
        return -1;
      }
      return 1;
    });

    const mapEntry = entry => {
      if (entry.contentType === 'inspirationArticle') {
        const btc = entry;
        btc.image = btc.btcThumbnail;
        btc.description = btc.btcShortDescription;
        btc.link = `/${locale}/inspiration/beauty-creators-talk/${entry.slug}`;
        return btc;
      }
      // eslint-disable-next-line no-param-reassign
      entry.link = `/${locale}/news-and-events/${entry.slug}`;
      return entry;
    };

    return (
      <ListSection>
        <DropdownMenu
          copy={copy}
          dropdowns={dropdowns.map(dropdown => ({
            ...dropdown,
            label: copy[dropdown.id] || '...',
          }))}
          onUpdate={this.handleUpdateDropdown}
        />

        {!(entries.slice(0, showing) && _.find(entries.slice(0, showing), item => _.get(item, 'slug'))) ? (
          [0, 1, 2].map(item => (
            <EntryContainer key={generateKey('entry', item)}>
              <Entry>
                <Intro>
                  <Title>
                    <Placeholder h="60px" w="100%" />
                  </Title>
                  <Placeholder w="100%" pb="130px" />
                </Intro>
                <Details>
                  <Type>
                    <Placeholder h="20px" w="200px" />
                  </Type>
                  <Description>
                    <Placeholder h="75px" w="100%" />
                  </Description>
                  <Date>
                    <Placeholder h="20px" w="200px" />
                  </Date>
                </Details>
              </Entry>
            </EntryContainer>
          ))
        ) : (
          <React.Fragment>
            {entries
              .slice(0, showing)
              .map(mapEntry)
              .map(entry => (
                <EntryContainer key={entry.title || entry.key}>
                  <Link to={entry.link} href={entry.link}>
                    <Entry key={entry.title || entry.key}>
                      <Intro>
                        <Title>{entry.title || <Placeholder h="60px" w="100%" />}</Title>
                        {entry.image ? (
                          <Image src={entry.image} options={{ size: 550, w: '100%' }} />
                        ) : (
                          <Placeholder w="100%" pb="130px" />
                        )}
                      </Intro>

                      <Details>
                        <Type>
                          {entry.type ? `${entry.type} / ${entry.location}` : <Placeholder h="20px" w="200px" />}
                        </Type>
                        <Description>{entry.description || <Placeholder h="75px" w="100%" />}</Description>
                        <Date>{entry.date || <Placeholder h="20px" w="200px" />}</Date>
                      </Details>
                    </Entry>
                  </Link>
                </EntryContainer>
            ))}
          </React.Fragment>
        )}

        {hasMore &&
          !loadingMore && (
            <Button onClick={this.handleLoadMore}>{copy.load_more || <Placeholder h="15px" w="100px" />}</Button>
          )}

        {loadingMore && (
          <Button>
            <Spinner show />
            <ButtonLabel>{`${copy.loading}...`}</ButtonLabel>
          </Button>
        )}
      </ListSection>
    );
  };
}

const mapState = (state, props) => ({
  locale: state.locale,
  entries: getArrayNewsAndEvents(state, { ...props, dataLevel: 2 }),
});

const mapDispatch = dispatch => ({
  fetchNewsAndEvents: params => dispatch(fetchNewsAndEvents(params)),
});

export default connect(mapState, mapDispatch)(List);



// WEBPACK FOOTER //
// ./src/pages/NewsAndEventsPage/ListSection/ListSection.js